import React, { createContext, useState, useContext, useEffect, useMemo } from "react";
import axios from "axios";
import { getToken } from "../utils/tokenUtils";

// Create the RecruitmentDashboardContext
const RecruitmentDashboardContext = createContext();

// Custom hook to use the RecruitmentDashboardContext
export const useRecruitmentDashboard = () => {
  const context = useContext(RecruitmentDashboardContext);
  if (!context) {
    throw new Error("useRecruitmentDashboard must be used within a RecruitmentDashboardProvider");
  }
  return context;
};

// Provider component to wrap the app
export const RecruitmentDashboardProvider = ({ children }) => {
  const [dashboardData, setDashboardData] = useState({
    interviewScheduledCount: 0,
    interviewFeedbackPendingCount: 0,
    approvalPendingCount: 0,
    offerAcceptancePendingCount: 0,
    candidates: {
      data: [],
      current_page: 1,
      last_page: 1,
      per_page: 15,
      total: 0,
      next_page_url: null,
      prev_page_url: null,
    },
    onboardings: [],
    jobs: [],
    top_campaigns: [],
    latest_campaigns: {
      data: [],
      current_page: 1,
      last_page: 1,
      per_page: 15,
      total: 0,
      next_page_url: null,
      prev_page_url: null,
    },
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const API_URL = "https://hrmspayroll.prepstripe.com/api/dashboard-counts";
  const token = getToken();

  // Fetch dashboard data (first page)
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        setLoading(true);
        setError(null);

        const response = await axios.get(API_URL, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        });

        const {
          interview_scheduled_count,
          interview_feedback_pending_count,
          approval_pending_count,
          offer_acceptance_pending_count,
          candidates,
          onboardings,
          jobs,
          top_campaigns,
          latest_campaigns,
        } = response.data;

        setDashboardData({
          interviewScheduledCount: interview_scheduled_count || 0,
          interviewFeedbackPendingCount: interview_feedback_pending_count || 0,
          approvalPendingCount: approval_pending_count || 0,
          offerAcceptancePendingCount: offer_acceptance_pending_count || 0,
          candidates: candidates || {
            data: [],
            current_page: 1,
            last_page: 1,
            per_page: 15,
            total: 0,
            next_page_url: null,
            prev_page_url: null,
          },
          onboardings: onboardings || [],
          jobs: jobs?.data || [],
          top_campaigns: top_campaigns || [],
          latest_campaigns: latest_campaigns || {
            data: [],
            current_page: 1,
            last_page: 1,
            per_page: 15,
            total: 0,
            next_page_url: null,
            prev_page_url: null,
          },
        });
      } catch (error) {
        setError(error.response?.data?.message || "Failed to load dashboard data. Please try again.");
      } finally {
        setLoading(false);
      }
    };
    fetchDashboardData();
  }, [API_URL, token]);

  // Fetch a specific page of latest_campaigns
  const fetchLatestCampaignsPage = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      const { latest_campaigns } = response.data;
      setDashboardData(prev => ({
        ...prev,
        latest_campaigns: latest_campaigns || {
          data: [],
          current_page: 1,
          last_page: 1,
          per_page: 15,
          total: 0,
          next_page_url: null,
          prev_page_url: null,
        },
      }));
    } catch (error) {
      setError(error.response?.data?.message || "Failed to load campaigns.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch a specific page of candidates
  const fetchCandidatesPage = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);
      const response = await axios.get(`${API_URL}?page=${page}`, {
        headers: {
          Authorization: `Bearer ${getToken()}`,
        },
      });
      const { candidates } = response.data;
      setDashboardData(prev => ({
        ...prev,
        candidates: candidates || {
          data: [],
          current_page: 1,
          last_page: 1,
          per_page: 15,
          total: 0,
          next_page_url: null,
          prev_page_url: null,
        },
      }));
    } catch (error) {
      setError(error.response?.data?.message || "Failed to load candidates.");
    } finally {
      setLoading(false);
    }
  };

  // Memoize the context value
  const value = useMemo(
    () => ({
      dashboardData,
      loading,
      error,
      fetchLatestCampaignsPage,
      fetchCandidatesPage,
    }),
    [dashboardData, loading, error]
  );

  return (
    <RecruitmentDashboardContext.Provider value={value}>
      {children}
    </RecruitmentDashboardContext.Provider>
  );
};
